import { useState, useRef, useCallback, useEffect } from 'react'
import { getAuthCredentials, MODULE_URLS } from '@/api/client'
import { multichannelApi } from '@/api/multichannel'
import { useAppStore } from '@/store/app'
import type { MessageStatus } from '@/hooks/useSupportChat'

const BASE = '/api/v1/multichannel'
const RECONNECT_DELAY_MS = 3_000

// Mirrors the wire protocol multichannel's handler/websocket.go already
// defines (types.WebSocketMessage) — same shape src/widget/chat-widget.ts
// uses, just consumed as a React hook here with the portal's real session
// token instead of an anonymous widget token.
interface WireMessage {
  Type: string
  id?: string
  chat_id?: string
  sender_id?: string
  recipient_id?: string
  content?: string
  direction?: string
  options?: string[]
}

export interface PortalChatMessage {
  id?: string
  content: string
  direction: 'incoming' | 'outgoing' | 'system'
  // Pre-chat questions only (Type 'prechat') — multiple-choice buttons to
  // show alongside the question; absent for free-text questions and every
  // other message type.
  options?: string[]
  sentAt: number
  // Outgoing-only; absent on incoming/system messages.
  status?: MessageStatus
}

interface UsePortalChatOptions {
  // 'support' for Mode 1 (any available agent), or a specific agent's user ID
  // for Mode 2 (the ticket's assignee).
  recipientId: string
  // Only set for Mode 2 — lets the server link the chat session to this
  // ticket and resume the same conversation across visits.
  ticketId?: string
  // Connects on mount when true. Both chat surfaces only render once their
  // toggle is enabled, but this also lets Mode 1's floating panel defer
  // connecting until the user actually opens it.
  enabled: boolean
}

export function usePortalChat({ recipientId, ticketId, enabled }: UsePortalChatOptions) {
  const { user } = useAppStore()
  const [messages, setMessages] = useState<PortalChatMessage[]>([])
  const [connected, setConnected] = useState(false)
  // Flips true once an agent resolves this conversation server-side — lets
  // the consuming UI show a closed banner and stop accepting replies.
  const [resolved, setResolved] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const chatIdRef = useRef<string | undefined>(undefined)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedByUsRef = useRef(false)

  // Hydrates an existing conversation on open/reconnect — without this, the
  // widget only ever learns about messages via live WebSocket events, so it
  // showed empty every time it reconnected (page refresh, re-login, closing
  // and reopening the popup) even though the conversation itself already
  // resumes under the same chat_id server-side. Prepended (not replacing)
  // ahead of whatever's already in `messages`, so a live message that
  // happens to arrive before this resolves is never clobbered.
  const fetchHistory = useCallback(async () => {
    try {
      const res = await multichannelApi.myChatSession(ticketId)
      if (res.chat_id) chatIdRef.current = res.chat_id
      if (res.resolved) setResolved(true)
      const own = (user?.email ?? '').toLowerCase()
      const mapped: PortalChatMessage[] = res.messages.map(m => ({
        id: m.id,
        content: m.content,
        direction: m.sender_id.toLowerCase() === own ? 'outgoing' : 'incoming',
        sentAt: new Date(m.sent_at).getTime(),
        ...(m.sender_id.toLowerCase() === own ? { status: 'seen' as MessageStatus } : {}),
      }))
      // De-duped by id against whatever's already in state — reopening the
      // popup within the same browser tab re-runs this against a `messages`
      // array that may already hold these same messages from a prior
      // open/close cycle, and id here is the real, stable chat_message id
      // (unlike the `local-*` placeholder ids used for not-yet-confirmed
      // outgoing sends), so this can't accidentally drop a real duplicate.
      if (mapped.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id).filter(Boolean))
          const newOnes = mapped.filter(m => !m.id || !existingIds.has(m.id))
          return newOnes.length > 0 ? [...newOnes, ...prev] : prev
        })
      }
    } catch {
      // Non-fatal — the live connection below still works even if history fails to load.
    }
  }, [ticketId, user?.email])

  // "I've seen everything in this chat so far" — sent on open/mount and
  // again whenever a new incoming message arrives while the panel is open,
  // so the agent's tick marks update live instead of waiting on a poll.
  const sendSeen = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !chatIdRef.current) return
    ws.send(JSON.stringify({ Type: 'seen', chat_id: chatIdRef.current }))
  }, [])

  const connect = useCallback(() => {
    const { token, orgId } = getAuthCredentials()
    if (!token || !orgId) return

    const apiUrl = MODULE_URLS.tickets || window.location.origin
    const url = new URL(apiUrl)
    const proto = url.protocol === 'https:' ? 'wss:' : 'ws:'
    // ValidateTokenWithIAM forwards this query param verbatim as the
    // Authorization header (see multichannel's handler/websocket.go) — needs
    // the "Bearer " prefix, same as connectSupportSocket's token param.
    const wsUrl = `${proto}//${url.host}${BASE}/ws?userType=customer&X-Org-ID=${encodeURIComponent(orgId)}&authToken=${encodeURIComponent(`Bearer ${token}`)}`

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => setConnected(true)

    ws.onmessage = event => {
      let msg: WireMessage
      try { msg = JSON.parse(event.data) } catch { return }

      if (msg.chat_id) chatIdRef.current = msg.chat_id

      if (msg.Type === 'system') {
        setMessages(prev => [...prev, {
          id: msg.id,
          content: msg.content ?? '',
          direction: 'system',
          sentAt: Date.now(),
        }])
        return
      }

      if (msg.Type === 'prechat') {
        setMessages(prev => [...prev, {
          id: msg.id,
          content: msg.content ?? '',
          direction: 'incoming',
          options: msg.options,
          sentAt: Date.now(),
        }])
        return
      }

      if (msg.Type === 'delivered' && msg.id) {
        setMessages(prev => prev.map(m => m.id === msg.id && m.status !== 'seen' ? { ...m, status: 'delivered' } : m))
        return
      }

      if (msg.Type === 'seen') {
        setMessages(prev => prev.map(m => m.direction === 'outgoing' ? { ...m, status: 'seen' } : m))
        return
      }

      if (msg.Type === 'resolved') {
        setResolved(true)
        setMessages(prev => [...prev, {
          id: msg.id,
          content: 'This conversation has been marked as resolved.',
          direction: 'system',
          sentAt: Date.now(),
        }])
        return
      }

      if (msg.Type !== 'text') return

      if (msg.direction === 'outgoing') {
        // Echo of our own message with its real, server-assigned id — adopt
        // it onto the oldest still-unconfirmed local copy so later
        // delivered/seen events (keyed by that real id) can find it.
        if (!msg.id) return
        setMessages(prev => {
          const idx = prev.findIndex(m => m.direction === 'outgoing' && m.id?.startsWith('local-'))
          if (idx === -1) return prev
          const updated = [...prev]
          updated[idx] = { ...updated[idx], id: msg.id }
          return updated
        })
        return
      }

      setMessages(prev => [...prev, {
        id: msg.id,
        content: msg.content ?? '',
        direction: 'incoming',
        sentAt: Date.now(),
      }])
      sendSeen()
    }

    ws.onclose = () => {
      setConnected(false)
      wsRef.current = null
      if (!closedByUsRef.current) {
        reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS)
      }
    }

    ws.onerror = () => ws.close()
  }, [])

  useEffect(() => {
    if (!enabled) return
    closedByUsRef.current = false
    fetchHistory()
    connect()
    return () => {
      closedByUsRef.current = true
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      wsRef.current?.close()
      wsRef.current = null
      setConnected(false)
    }
  }, [enabled, connect, fetchHistory])

  // Marks the chat seen whenever it has something to be seen of — on
  // (re)connect/resume, and again on each new incoming message (handled
  // separately in onmessage, since that fires after chatIdRef is populated).
  useEffect(() => {
    if (connected) sendSeen()
  }, [connected, sendSeen])

  const sendMessage = useCallback((content: string) => {
    const trimmed = content.trim()
    const ws = wsRef.current
    if (!trimmed || !ws || ws.readyState !== WebSocket.OPEN || resolved) return

    setMessages(prev => [...prev, {
      id: `local-${Date.now()}`,
      content: trimmed,
      direction: 'outgoing',
      status: 'sent',
      sentAt: Date.now(),
    }])

    ws.send(JSON.stringify({
      Type: 'text',
      recipient_id: recipientId,
      content: trimmed,
      ...(chatIdRef.current ? { chat_id: chatIdRef.current } : {}),
      ...(ticketId ? { ticket_id: ticketId } : {}),
    }))
  }, [recipientId, ticketId, resolved])

  return { messages, sendMessage, connected, markSeen: sendSeen, resolved }
}
